import { Injectable } from '@angular/core';
import { Functions, httpsCallableData } from '@angular/fire/functions';
import { Observable, concat, firstValueFrom, from, map, of } from 'rxjs';

// The callable access-checks and signs per batch; keep requests within its cap.
const CHUNK_SIZE = 50;
const FALLBACK_TTL_MS = 60 * 60 * 1000;
// Treat a URL as expired slightly early so we re-sign before it actually dies.
const EXPIRY_SKEW_MS = 60 * 1000;
// Dashboard cards resolve their photos independently and land within a few
// milliseconds of each other. Holding ids for a short window collapses those
// into a single callable instead of one invocation (and one rate-limit
// transaction) per card.
const BATCH_WINDOW_MS = 50;

interface GetUserPhotoUrlsResponse {
  urls: Record<string, string>;
  expiresAt: number;
}

interface PhotoCacheEntry {
  url: string | null;
  expiresAt: number;
}

type PhotoBackedItem = {
  id?: string;
  photoUrl?: string | null;
  profilePic?: string | null;
};

type AttachOptions = { profilePic?: boolean; forceRefresh?: boolean };

/**
 * Resolves profile photo URLs for a set of users.
 *
 * URLs are short-lived signed URLs minted by the `getUserPhotoUrls` callable,
 * which access-checks every requested user. Photos are no longer readable from a
 * permanently stored URL, so results are cached only until they expire.
 *
 * Lookups are batched: ids requested within `BATCH_WINDOW_MS` of each other go
 * out as one call, so a dashboard full of avatar-bearing cards costs a single
 * round trip rather than one per card.
 */
@Injectable({
  providedIn: 'root'
})
export class UserPhotoLookupService {
  private readonly cache = new Map<string, PhotoCacheEntry>();
  // Ids waiting for the current batch window to close.
  private readonly queued = new Set<string>();
  // Ids already sent, mapped to the request that will resolve them.
  private readonly inFlight = new Map<string, Promise<void>>();
  private queuedFlush?: Promise<void>;

  constructor(private functions: Functions) { }

  /**
   * Resolves photo URLs for the given users.
   *
   * @param userIds Users to resolve.
   * @param forceRefresh Re-fetch even if cached — use after changing a photo, so
   * the newly stored image is picked up instead of a stale entry.
   */
  public getPhotoUrls$(
    userIds: (string | undefined)[],
    forceRefresh = false,
  ): Observable<Map<string, string | null>> {
    const uniqueIds = this.uniqueIds(userIds);

    if (!uniqueIds.length) {
      return of(new Map());
    }

    if (forceRefresh) {
      uniqueIds.forEach(id => this.cache.delete(id));
      // Bypass the batcher: an in-flight batch was signed before the photo
      // changed, so joining it would resolve to the superseded image.
      return from(this.fetchIds(uniqueIds)).pipe(
        map(() => this.buildResult(uniqueIds)),
      );
    }

    const missingIds = this.unresolvedIds(uniqueIds);

    if (!missingIds.length) {
      return of(this.buildResult(uniqueIds));
    }

    return from(this.resolveMissing(missingIds)).pipe(
      map(() => this.buildResult(uniqueIds)),
    );
  }

  /**
   * Attaches photo URLs to a list of items.
   *
   * Emits the items straight away — carrying whatever is already cached — and
   * again once the lookup resolves. Callers that pipe this into a view render on
   * the first emission instead of holding a loading skeleton in front of the
   * whole round trip; use `lastValueFrom` where the resolved URLs are required.
   */
  public attachPhotoUrls$<T extends PhotoBackedItem>(
    items: T[],
    options: AttachOptions = {},
  ): Observable<T[]> {
    const ids = items.map(item => item.id);
    const resolved$ = this.getPhotoUrls$(ids, options.forceRefresh).pipe(
      map(photoUrls => this.applyPhotoUrls(items, photoUrls, options)),
    );

    const needsLookup = options.forceRefresh || this.unresolvedIds(this.uniqueIds(ids)).length > 0;

    if (!needsLookup) {
      // Everything is cached, so `resolved$` emits synchronously — no need for a
      // separate placeholder emission.
      return resolved$;
    }

    return concat(
      of(this.applyPhotoUrls(items, this.buildResult(this.uniqueIds(ids)), options)),
      resolved$,
    );
  }

  private applyPhotoUrls<T extends PhotoBackedItem>(
    items: T[],
    photoUrls: Map<string, string | null>,
    options: AttachOptions,
  ): T[] {
    return items.map(item => {
      if (!item.id || !photoUrls.has(item.id)) {
        return item;
      }

      const photoUrl = photoUrls.get(item.id) ?? undefined;
      return {
        ...item,
        photoUrl,
        ...(options.profilePic ? { profilePic: photoUrl } : {}),
      };
    });
  }

  private uniqueIds(userIds: (string | undefined)[]): string[] {
    return [...new Set(userIds.filter((id): id is string => !!id))];
  }

  // Ids with no usable cache entry. Signed URLs stop working once they expire, so
  // an entry is dropped shortly before its expiry and re-signed on next use.
  private unresolvedIds(ids: string[]): string[] {
    const now = Date.now();

    return ids.filter(id => {
      const entry = this.cache.get(id);

      if (!entry) {
        return true;
      }

      if (entry.expiresAt - EXPIRY_SKEW_MS <= now) {
        this.cache.delete(id);
        return true;
      }

      return false;
    });
  }

  private buildResult(ids: string[]): Map<string, string | null> {
    const result = new Map<string, string | null>();

    for (const id of ids) {
      result.set(id, this.cache.get(id)?.url ?? null);
    }

    return result;
  }

  // Joins whatever request already covers each id, queueing the rest for the
  // next batch, so overlapping cards never re-request the same user.
  private resolveMissing(ids: string[]): Promise<void> {
    const waits: Promise<void>[] = [];
    let queuedAny = false;

    for (const id of ids) {
      const pending = this.inFlight.get(id);

      if (pending) {
        waits.push(pending);
        continue;
      }

      this.queued.add(id);
      queuedAny = true;
    }

    if (queuedAny) {
      waits.push(this.scheduleFlush());
    }

    return Promise.all(waits).then(() => undefined);
  }

  private scheduleFlush(): Promise<void> {
    if (!this.queuedFlush) {
      this.queuedFlush = new Promise<void>(resolve => {
        setTimeout(() => {
          // Cleared before flushing so ids requested from here on open a new
          // batch rather than attaching to one already going out.
          this.queuedFlush = undefined;
          void this.flush().then(resolve, resolve);
        }, BATCH_WINDOW_MS);
      });
    }

    return this.queuedFlush;
  }

  private async flush(): Promise<void> {
    const ids = [...this.queued];
    this.queued.clear();

    if (!ids.length) {
      return;
    }

    const request = this.fetchIds(ids);
    ids.forEach(id => this.inFlight.set(id, request));

    try {
      await request;
    } finally {
      ids.forEach(id => {
        if (this.inFlight.get(id) === request) {
          this.inFlight.delete(id);
        }
      });
    }
  }

  private async fetchIds(ids: string[]): Promise<void> {
    await Promise.all(this.chunk(ids, CHUNK_SIZE).map(idChunk => this.fetchChunk(idChunk)));
  }

  private async fetchChunk(ids: string[]): Promise<void> {
    try {
      const response = await firstValueFrom(this.callGetUserPhotoUrls(ids));
      const urls = response?.urls ?? {};
      const expiresAt = response?.expiresAt || Date.now() + FALLBACK_TTL_MS;

      for (const id of ids) {
        this.cache.set(id, { url: urls[id] ?? null, expiresAt });
      }
    } catch (error) {
      // Leave these ids out of the cache so a later call retries instead of
      // permanently failing to load a photo after a transient error.
      console.error('Failed to look up user photos', error);
    }
  }

  private callGetUserPhotoUrls(userIds: string[]): Observable<GetUserPhotoUrlsResponse> {
    const fn = httpsCallableData<{ userIds: string[] }, GetUserPhotoUrlsResponse>(
      this.functions,
      'getUserPhotoUrls'
    );
    return fn({ userIds });
  }

  private chunk<T>(values: T[], size: number): T[][] {
    const chunks: T[][] = [];

    for (let index = 0; index < values.length; index += size) {
      chunks.push(values.slice(index, index + size));
    }

    return chunks;
  }
}
